/* eslint-disable @typescript-eslint/no-unused-vars */
import {create} from 'zustand';
import {RawRule, subject, AbilityBuilder, createMongoAbility, MongoQuery} from '@casl/ability';
import useLoginStore from './useLoginStore';
import {TaskMemberTypes} from '@/pages/resources/tasks/types';
import {ProjectMemberTypes} from '@/pages/resources/projects/types';
import {BaseRecord} from '@refinedev/core';

export interface MyAbility {
  can: (a: string, s: string, r?: any) => boolean;
  cannot: (a: string, s: string, r?: any) => boolean;
}

export interface AbilityStore extends MyAbility {
  ability?: MyAbility;
  ability2?: MyAbility; //additional ability
  setAbility: (ability: MyAbility) => void;
  setAbility2: (ability: MyAbility) => void;
  unsetAbility: () => void;
}

export function buildAbility(rules: RawRule[]) {
  const {can, cannot, build} = new AbilityBuilder(createMongoAbility);
  rules.forEach(rule => {
    if (rule.inverted) {
      cannot(rule.action, rule.subject, rule.fields, rule.conditions as MongoQuery<any>);
    } else {
      can(rule.action, rule.subject, rule.fields, rule.conditions as MongoQuery<any>);
    }
  });
  return build();
}

export function buildRulesFromUserdata(userdata?: any) {
  if (!userdata) userdata = useLoginStore.getState().userdata;
  if (!userdata) return [];

  const {roles: userRoles, permissions} = userdata;
  // define ability
  const rules: RawRule[] = [];
  if (!userRoles) return rules; // new Ability(); //empty
  for (var p of permissions) {
    if (p === '*') {
      rules.push({action: 'manage', subject: 'all'});
      return rules;
    }

    const ss = p.split('.');
    if (ss.length >= 2) {
      let action = ss[1];
      if (action === '*') action = 'manage';
      let subject = ss[0];
      if (subject === '*') subject = 'all';
      rules.push({action, subject});
    }
  }
  return rules;
}

const taskRules: RawRule[] = [
  {action: 'read', subject: 'tasks', conditions: {my_role: {$in: TaskMemberTypes.map(t => t.id)}}},
  {
    action: 'read',
    subject: 'tasks',
    conditions: {my_project_role: {$in: ProjectMemberTypes.map(t => t.id)}},
  },
  {action: 'update', subject: 'tasks', conditions: {my_role: {$eq: 'editor'}}},
  {action: 'update', subject: 'tasks', conditions: {my_project_role: {$eq: 'manager'}}},
  {action: 'create', subject: 'tasks', conditions: {my_project_role: {$eq: 'manager'}}}, // can create if project manager
  {action: 'delete', subject: 'tasks', conditions: {my_project_role: {$eq: 'manager'}}}, // can create if project manager
];

const projectRules: RawRule[] = [
  {
    action: 'read',
    subject: 'projects',
    conditions: {my_role: {$in: ProjectMemberTypes.map(t => t.id)}},
  },
  {action: 'update', subject: 'projects', conditions: {my_role: {$eq: 'manager'}}},
];

const projectContractRules: RawRule[] = [
  {
    action: 'read',
    subject: 'project_contracts',
    conditions: {my_project_role: {$in: ProjectMemberTypes.map(t => t.id)}},
  },
  {action: 'update', subject: 'project_contracts', conditions: {my_project_role: {$eq: 'manager'}}},
  {action: 'create', subject: 'project_contracts', conditions: {my_project_role: {$eq: 'manager'}}},
  {action: 'delete', subject: 'project_contracts', conditions: {my_project_role: {$eq: 'manager'}}},
];

export function getAbilityFromUserdata(userdata?: any, otherRules: RawRule[] = []): MyAbility {
  const rules1 = buildRulesFromUserdata(userdata);
  const rules2 = [...taskRules, ...projectRules, ...projectContractRules];
  const rules = [...rules1, ...rules2, ...otherRules];
  const ability = buildAbility(rules);
  console.log('ability', ability);
  const my_ability = {
    can: (action: string, s: string, instance?: any) => {
      const output = instance ? ability.can(action, subject(s, instance)) : ability.can(action, s);
      // console.log('can', action, s, instance, output);
      return output;
    },
    cannot: (action: string, s: string, instance?: any) => {
      const output = instance
        ? ability.cannot(action, subject(s, instance))
        : ability.cannot(action, s);
      // console.log('cannot', action, s, instance, output);
      return output;
    },
  };
  (window as any).my_ability = my_ability; // for debugging
  return my_ability;
}

export function getRulesFromData(subject: string, data: BaseRecord) {
  if (subject === 'projects') {
    const new_rules: RawRule[] = [];

    if (data.my_role === 'manager')
      new_rules.push({
        action: 'update',
        subject: 'projects',
        inverted: false,
        conditions: {id: {$eq: data.id}},
      });

    return [new_rules];
  }
  return [];
}

const useAbilityStore = create<AbilityStore>()((set, get) => ({
  ability: undefined,
  ability2: undefined,

  setAbility(ability: MyAbility) {
    set({ability});
  },
  setAbility2(ability: MyAbility) {
    set({ability2: ability});
  },
  unsetAbility() {
    set({ability: undefined, ability2: undefined});
  },
  can: (action: string, s: string, instance?: any) => {
    const can1 = get().ability?.can(action, s, instance) ?? false;
    const can2 = get().ability2?.can(action, s, instance) ?? false;
    return can1 || can2;
  },
  cannot: (action: string, s: string, instance?: any) => {
    const cannot1 = get().ability?.cannot(action, s, instance) ?? false;
    const cannot2 = get().ability2?.cannot(action, s, instance) ?? false;
    return cannot1 || cannot2;
  },
}));

export default useAbilityStore;
